// apps/web/src/features/coupons/hooks/useCoupons.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import { getISTToday } from '@lov/shared';
import type {
  CouponFilterQuery,
  CreateCouponRequest,
  UpdateCouponRequest,
  ValidateCouponRequest,
  AvailableCouponsQuery,
  CouponResponse,
  CouponValidationResult,
  AvailableCouponsResult,
} from '@lov/shared';

interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Admin hooks
export function useCoupons(params: Partial<CouponFilterQuery> = {}) {
  return useQuery<PaginatedResult<CouponResponse>>({
    queryKey: ['coupons', params],
    queryFn: async () => {
      return validatedApi.get('/coupons', { params });
    },
  });
}

export function useCoupon(id: string) {
  return useQuery<CouponResponse>({
    queryKey: ['coupons', id],
    queryFn: async () => {
      return validatedApi.get(`/coupons/${id}`);
    },
    enabled: !!id,
  });
}

export function useCreateCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCouponRequest) => {
      return validatedApi.post('/coupons', dto);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

export function useUpdateCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateCouponRequest & { id: string }) => {
      return validatedApi.put(`/coupons/${id}`, dto);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

export function useDeleteCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/coupons/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

// Vendor hooks
export function useVendorCoupons(params: Partial<CouponFilterQuery> = {}) {
  return useQuery<PaginatedResult<CouponResponse>>({
    queryKey: ['vendor-coupons', params],
    queryFn: async () => {
      return validatedApi.get('/vendor/coupons', { params });
    },
  });
}

export function useCreateVendorCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCouponRequest) => {
      return validatedApi.post('/vendor/coupons', dto);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vendor-coupons'] });
    },
  });
}

export function useUpdateVendorCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateCouponRequest & { id: string }) => {
      return validatedApi.put(`/vendor/coupons/${id}`, dto);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vendor-coupons'] });
    },
  });
}

export function useDeleteVendorCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/vendor/coupons/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vendor-coupons'] });
    },
  });
}

// Customer hooks (for booking flow)
export function useValidateCoupon() {
  return useMutation<CouponValidationResult, Error, ValidateCouponRequest>({
    mutationFn: async (dto) => {
      return validatedApi.post('/coupons/validate', dto);
    },
  });
}

export function useAvailableCoupons(
  params: AvailableCouponsQuery,
  options?: { enabled?: boolean }
) {
  return useQuery<AvailableCouponsResult>({
    queryKey: ['coupons', 'available', params],
    queryFn: async () => {
      return validatedApi.get('/coupons/available', { params });
    },
    enabled: options?.enabled ?? (!!params.venueId && !!params.bookingDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook for fetching coupons for venue preview (without booking context)
export function useVenueCoupons(venueId: string, options?: { enabled?: boolean }) {
  const today = getISTToday();
  return useAvailableCoupons(
    {
      venueId,
      bookingDate: today,
      subtotal: 0, // Returns all coupons with status (eligible/locked/nearly_unlocked)
    },
    { enabled: options?.enabled ?? !!venueId }
  );
}
